import React, { useEffect, useMemo, useState } from 'react';
import { ProcoreCompanyChoice, tenancyApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePaginatedList } from '../../hooks/usePagination';
import { Link, useLocation, useNavigate } from '../../router/Router';
import { IconShield, IconSync } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';

export function LoginPage() {
  const {
    user,
    company,
    hasClientToken,
    portalMode,
    maintenanceMode,
    refreshUserSession,
    loginClientWithToken,
  } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const isCompanyPickerRoute =
    Boolean(user || hasClientToken) && location.pathname === '/auth/procore-company';

  const [companies, setCompanies] = useState<ProcoreCompanyChoice[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectingCompanyId, setSelectingCompanyId] = useState<string | null>(null);
  const [companySearch, setCompanySearch] = useState<string>('');

  const filteredCompanies = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => {
      const procoreCompanyId = String(c.procore_company_id || c.id || '').toLowerCase();
      return c.name.toLowerCase().includes(q) || procoreCompanyId.includes(q);
    });
  }, [companies, companySearch]);

  const companiesPagination = usePaginatedList(filteredCompanies, 10);

  useEffect(() => {
    if (isCompanyPickerRoute) {
      setLoadingCompanies(true);
      tenancyApi
        .listProcoreCompanies()
        .then((list) => setCompanies(list))
        .catch(() => setCompanies([]))
        .finally(() => setLoadingCompanies(false));
    }
  }, [isCompanyPickerRoute]);

  const handleProcoreOAuthLogin = () => {
    const oauthUrl = tenancyApi.getOAuthStartUrl('procore');
    window.location.href = oauthUrl;
  };

  const handleSelectCompany = async (choice: ProcoreCompanyChoice) => {
    const procoreCompanyId = choice.procore_company_id || String(choice.id);
    setSelectingCompanyId(procoreCompanyId);
    try {
      const res = await tenancyApi.selectProcoreCompany(procoreCompanyId);
      if (res.auth_token_id) {
        await loginClientWithToken(res.auth_token_id);
      } else {
        await refreshUserSession();
      }
      pushToast({
        tone: 'success',
        title: 'Procore company selected',
        description: `Switched workspace to ${choice.name}.`,
      });
      navigate('/projects');
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not switch Procore company',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSelectingCompanyId(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md">
            <IconSync className="w-7 h-7" />
          </div>
        </div>
        <h1 className="mt-4 text-center text-2xl font-bold tracking-tight text-slate-900">
          {isCompanyPickerRoute ? 'Select Your Procore Company' : 'Sign in to Sync Platform'}
        </h1>
        <p className="mt-1.5 text-center text-sm text-slate-600">
          {isCompanyPickerRoute
            ? 'Choose which Procore company tenant to connect with Smartsheet'
            : 'Bidirectional schedule, task, submittal & RFI synchronization'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        {maintenanceMode && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-900">
            <p className="font-semibold">System Maintenance Active</p>
            <p className="mt-1">
              Background synchronization jobs are currently paused while maintenance is in progress.
            </p>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          {isCompanyPickerRoute ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Available Procore Companies
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Select the Procore company workspace you want to manage.
                </p>
              </div>

              {companies.length > 5 && (
                <div>
                  <input
                    type="search"
                    aria-label="Search Procore companies"
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      companiesPagination.setPage(0);
                    }}
                    placeholder="Search company name or Procore ID…"
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              )}

              {loadingCompanies ? (
                <div className="py-8 text-center text-sm text-slate-500">
                  Loading Procore companies…
                </div>
              ) : companies.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500 space-y-3">
                  <p>No Procore companies were found for your account.</p>
                  <button
                    type="button"
                    onClick={handleProcoreOAuthLogin}
                    className="inline-flex items-center justify-center rounded-lg bg-[#f45800] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#d94e00]"
                  >
                    Reconnect Procore OAuth
                  </button>
                </div>
              ) : (
                <div>
                  {filteredCompanies.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                      No Procore companies match your search.
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200">
                      {companiesPagination.pageItems.map((c) => {
                        const procoreCompanyId = c.procore_company_id || String(c.id);
                        const isSelecting = selectingCompanyId === procoreCompanyId;
                        return (
                          <li
                            key={procoreCompanyId}
                            className="flex items-center justify-between gap-4 p-3.5 hover:bg-slate-50"
                          >
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{c.name}</p>
                              <p className="text-[11px] font-mono text-slate-500 tabular-nums">
                                Procore #{procoreCompanyId}
                              </p>
                            </div>
                            <button
                              type="button"
                              disabled={Boolean(selectingCompanyId)}
                              onClick={() => void handleSelectCompany(c)}
                              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {isSelecting ? 'Opening…' : 'Open Workspace →'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <PaginationBar
                    pagination={companiesPagination}
                    itemLabel="companies"
                    compact
                    className="rounded-xl border border-slate-200 mt-3"
                  />
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <Link to="/projects" className="text-xs font-medium text-slate-600 hover:text-slate-900">
                  ← Back to Projects
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {user && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-emerald-950">
                      Active Session: {user.full_name || user.email}
                    </p>
                    <p className="text-xs text-emerald-800 mt-0.5">
                      {company ? `Company: ${company.name} (#${company.procore_company_id})` : user.email}
                    </p>
                  </div>
                  <Link
                    to="/projects"
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                  >
                    Continue to Dashboard →
                  </Link>
                </div>
              )}

              {/* Primary OAuth Button */}
              <div>
                <button
                  type="button"
                  onClick={handleProcoreOAuthLogin}
                  className="w-full flex items-center justify-center gap-3 rounded-xl bg-[#f45800] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#d94e00] transition-colors"
                >
                  <span>Continue with Procore OAuth</span>
                </button>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>Redirects to Procore authorization</span>
                  {user && (
                    <Link
                      to="/auth/procore-company"
                      className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Switch Procore Company →
                    </Link>
                  )}
                </div>
              </div>

              {portalMode !== 'client' && (
                <div className="border-t border-slate-200 pt-4 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <IconShield className="w-4 h-4 text-indigo-600" />
                    <span>Platform Administrator?</span>
                  </span>
                  <Link
                    to="/admin/login"
                    className="font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Open Admin Console →
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
